const BASE_URL = '/api'

export class ApiError extends Error {
  status: number
  code: string
  body: Record<string, unknown>

  constructor(status: number, code: string, message: string, body: Record<string, unknown> = {}) {
    super(message)
    this.status = status
    this.code = code
    this.body = body
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${BASE_URL}${path}`
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'UNKNOWN', message: res.statusText }))
    throw new ApiError(res.status, body.error, body.message, body)
  }

  if (res.headers.get('content-type')?.includes('application/json')) {
    return res.json()
  }

  return res.text() as unknown as T
}

export const api = {
  get: <T>(path: string, init?: Omit<RequestInit, 'method' | 'body'>) =>
    request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, data?: unknown, init?: Omit<RequestInit, 'method' | 'body'>) =>
    request<T>(path, {
      ...init,
      method: 'POST',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  put: <T>(path: string, data?: unknown, init?: Omit<RequestInit, 'method' | 'body'>) =>
    request<T>(path, {
      ...init,
      method: 'PUT',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  patch: <T>(path: string, data?: unknown, init?: Omit<RequestInit, 'method' | 'body'>) =>
    request<T>(path, {
      ...init,
      method: 'PATCH',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  delete: <T>(path: string, init?: Omit<RequestInit, 'method' | 'body'>) =>
    request<T>(path, { ...init, method: 'DELETE' }),
}

// Types for API responses
export type ListResponse<T> = {
  result: T[]
  nextCursor?: string
  prevCursor?: string
  total?: number
}

export type Task = {
  id: number
  name: string
  description: string | null
  current_version: number
  status: string
  created_at: string
  updated_at: string | null
  current_yaml?: string | null
}

export type TaskValidationIssue = {
  severity: string
  message: string
  step?: string
  field?: string
}

export type TaskValidationResult = {
  valid: boolean
  step_count?: number
  connectors?: string[]
  issues: TaskValidationIssue[]
}

export type TaskVersion = {
  id: number
  task_id: number
  version: number
  yaml_content: string
  connector_refs: string | null
  change_summary: string | null
  created_at: string
}

export type StepMetrics = {
  items_in: number
  items_out: number
  errors: number
  skipped: number
  avg_duration_ms: number
}

export type ConnectorMetrics = {
  calls: number
  errors: number
  avg_duration_ms: number
}

export type RunMetricsSummary = {
  items_total: number
  duration_ms: number
  steps: Record<string, StepMetrics>
  connectors: Record<string, ConnectorMetrics>
}

export type Run = {
  id: number
  task_id: number
  task_version: number
  trigger_type: string
  trigger_id: number | null
  status: string
  arguments: string | null
  connector_mapping: string | null
  pid: number | null
  item_count: number | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  log_file: string | null
  metrics_summary: string | null
  executor_id: string | null
  created_at: string
  is_active?: boolean
}

export type Connector = {
  id: number
  name: string
  type: string
  config: string | Record<string, unknown>
  description: string | null
  created_at: string
  updated_at: string | null
}

export type FieldSchema = {
  type: 'string' | 'number' | 'boolean' | 'string[]' | 'number[]' | 'object' | 'object[]'
  required?: boolean
  secret?: boolean
  description?: string
  default?: unknown
  items?: Record<string, FieldSchema>
}

export type ConnectorFileCapabilities = {
  upload: boolean
  accepted_extensions?: string[]
  max_bytes?: number
}

export type ConnectorCapabilities = {
  files?: ConnectorFileCapabilities
}

export type ConnectorTypeInfo = {
  type: string
  label: string
  configSchema: Record<string, FieldSchema>
  capabilities?: ConnectorCapabilities
  integrated_account_explorer?: boolean
  catalog?: boolean
  integration_slug?: string
  /** Catalog logo/icon URL for catalog-powered types. */
  icon_url?: string
}

export type OAuthAppStatus = {
  needs_byoa: boolean
  configured: boolean
  oauth_app_mode: 'platform_default' | 'byoa' | null
  client_id?: string
  format: string | null
  installed_integration_id?: string
  /** Saffron OAuth callback URL to register on the customer OAuth app. */
  callback_url?: string | null
}

export type OAuthAppSummary = OAuthAppStatus & {
  slug: string
  label: string
  icon_url?: string
  auth_format: string | null
}

export type Trigger = {
  id: number
  job_id: number
  task_key: string | null
  type: string
  cron_expression: string | null
  arguments: string | null
  enabled: number
  last_triggered_at: string | null
  created_at: string
  updated_at: string | null
}

export type AiReference = {
  id: number
  slug: string
  title: string
  keywords: string | null
  content: string
  enabled: number
  created_at: string
  updated_at: string | null
}

export type MetricsConfig = {
  id: number
  backend: string
  config: string
  enabled: number
  created_at: string
  updated_at: string | null
}

export type AuthStatus = {
  needsSetup: boolean
  totp_required: boolean
}

export type SetupResponse = {
  totp_secret?: string
  totp_uri?: string
  success?: boolean
}

export type LoginResponse = {
  requires_totp?: boolean
  needs_totp_setup?: boolean
  can_restart_totp_setup?: boolean
  authenticated?: boolean
}

export type AuthProvidersResponse = {
  providers: string[]
}

export type UserRole = 'superadmin' | 'admin' | 'viewer'

export type User = {
  id: string
  username: string
  role: UserRole
}

export type UserDetail = {
  id: number
  username: string
  role: UserRole
  totp_verified: number
  invite_pending: boolean
  created_at: string
  updated_at: string | null
}

export type CreateUserPayload = {
  username: string
  role?: 'admin' | 'viewer'
}

export type CreateUserResponse = UserDetail & { invite_token: string }

export type InviteValidation = {
  username: string
  expires_at: string
  totp_pending?: boolean
}

export type RegenerateInviteResponse = {
  invite_token: string
}

export type SetupTotpResponse = {
  totp_secret: string
  totp_uri: string
}

export type UpdateUserPayload = {
  username?: string
  password?: string
  role?: 'admin' | 'viewer'
}

export type ChangePasswordPayload = {
  current_password: string
  new_password: string
}

export type TotpReEnrollResponse = {
  totp_secret: string
  totp_uri: string
}

export type JobTemplate = {
  id: number
  name: string
  description: string | null
  yaml_content: string
  status: string
  created_at: string
  updated_at: string | null
  dag?: {
    tasks: Record<string, { task: string; type?: string; depends_on?: string[]; description?: string; connector_mapping?: Record<string, string> }>
    levels: string[][]
  }
  connectors?: Record<string, { type: string }>
  arguments?: Record<string, {
    type: string
    required?: boolean
    description?: string
    default?: unknown
    enum?: unknown[]
    widget?: string
    integration?: string
    connector_slot?: string
    mongo_collection?: string
    mongo_value_field?: string
    mongo_label_field?: string
    mongo_description_fields?: string[]
    mongo_search_fields?: string[]
    mongo_filter?: Record<string, unknown>
    mongo_sort?: Record<string, 1 | -1>
  }>
  job_config?: Record<string, JobConfigEntry> | null
}

export type Job = {
  id: number
  job_template_id: number
  name: string
  description: string | null
  connector_mapping: Record<string, number>
  resolved_connectors?: Record<string, { id: number; name?: string; type?: string }>
  arguments?: Record<string, unknown> | null
  status: string
  created_at: string
  updated_at: string | null
  template_name?: string | null
  job_config?: Record<string, unknown>
  overridden_task_keys?: string[]
  dag?: {
    tasks: Record<string, { task: string; type?: string; depends_on?: string[]; description?: string; connector_mapping?: Record<string, string> }>
    levels: string[][]
  }
}

export type JobRun = {
  id: number
  job_id: number
  status: string
  selected_tasks: string | null
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  error_message: string | null
  run_state: string | null
  skip_deps?: number | null
  created_at: string
  updated_at: string | null
}

export type JobRunTask = {
  id: number
  job_run_id: number
  task_key: string
  task_type: string | null
  run_id: number | null
  status: string
  task_result: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string | null
}

export type TaskResultMessage = {
  level: 'info' | 'warning' | 'error'
  message: string
  context?: Record<string, unknown>
}

export type MetricsSummary = {
  available: boolean
  task: string
  total_runs?: number
  success_count?: number
  error_count?: number
  total_items?: number
  duration?: {
    avg_ms: number | null
    min_ms: number | null
    max_ms: number | null
  }
  last_run_at?: string | null
}

export type MetricsTimePoint = {
  day: string
  success: number
  error: number
  avg_duration_ms: number
  total_items: number
}

export type MetricsTimeseries = {
  available: boolean
  task: string
  points: MetricsTimePoint[]
}

export type ExecutorInfo = {
  url: string
  maxConcurrent: number
  activeTasks: number
  labels: Record<string, string | boolean | number>
  lastHeartbeat: number
  healthy: boolean
}

export type ServerInfo = {
  executor: { type: 'local' | 'http'; callback_url?: string }
  runs: { max_concurrent: number }
  connectors?: { upload_max_bytes?: number }
  tasks_dir: string
  jobs_dir: string
}

export type ApiKey = {
  id: number
  name: string
  key_prefix: string
  created_by: number
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

export type CreateApiKeyResponse = ApiKey & { key: string }

export type BulkDeleteResponse = {
  deleted_count: number
  skipped_count: number
  skipped: { id: number; reason: string }[]
}

// Connector test types
export type ConnectorCheckError = {
  name: string
  message: string
  code?: string
}

export type ConnectorCheck = {
  name: string
  ok: boolean
  message?: string
  error?: ConnectorCheckError
  duration_ms: number
}

export type ConnectorTestResult = {
  ok: boolean
  checks: ConnectorCheck[]
}

// Data Explorer types
export type IntrospectRequest = {
  resource?: string
  integrated_account_id?: string
  surface?: 'all' | 'unified' | 'proxy'
  limit?: number
  cursor?: string
  q?: string
  integration?: string
  /** 'list' returns one page of resource rows (integrated-account explorer types) instead of the column schema */
  mode?: 'list'
  /** Query key that receives `q` in list mode (default 'query' — e.g. Notion search) */
  search_param?: string
  /** D365 only — bypass the connector's cached entity metadata and refetch */
  refresh?: boolean
}

export type IntrospectAccount = {
  id: string
  label?: string | null
  tenant_id: string
  integration_name?: string
  status?: string | null
}

export type IntrospectResource = {
  name: string
  type?: string
  methods?: string[]
}

export type IntrospectColumn = {
  name: string
  type: string
  nullable?: boolean
  enum?: string[]
}

export type IntrospectResponse = {
  resources?: IntrospectResource[]
  columns?: IntrospectColumn[]
  accounts?: IntrospectAccount[]
  /** Raw resource rows — list mode only */
  items?: unknown[]
  next_cursor?: string | null
  /** True when `resources` is a hardcoded fallback list because the live schema fetch failed */
  fallback?: boolean
  /** Error message from the underlying schema fetch, when it failed */
  schema_error?: string
}

export type QueryRequest = {
  method: string
  args: Record<string, unknown>
  limit?: number
}

export type QueryResponse = {
  data: Record<string, unknown>[]
  count: number
  truncated: boolean
  mutation?: boolean
  next_cursor?: string | null
  prev_cursor?: string | null
}

export type { PresetParameter, ExplorerPresetDefinition, ExplorerPreset } from '@/lib/explorer-preset'

export type DashboardTemplate = {
  id: number
  job_template_id: number
  name: string
  description: string | null
  yaml_content: string
  status: string
  created_at: string
  updated_at: string | null
  linked_dashboard_count?: number
}

export type DashboardWidgetType =
  | 'stat'
  | 'progress'
  | 'steps'
  | 'people'
  | 'bar_chart'
  | 'line_chart'
  | 'pie_chart'
  | 'table'
  | 'heading'
  | 'field_mapping'
  | 'form'
  | 'action'
  | 'task_progress'
  | 'explorer'

export type DashboardTableColumn = {
  field: string
  label?: string
  format?: string
  /** Explorer: when true, header is sortable (server-side, indexed fields only). */
  sortable?: boolean
}

export type FieldMappingExtraColumn = {
  key: string
  label?: string
  type: 'text' | 'select' | 'boolean'
  options?: { label: string; value: unknown }[]
  required?: boolean
}

export type DashboardActionOnSuccess = {
  refresh?: string[]
  message?: string
}

export type DashboardPage = {
  id: string
  title: string
  widgets: DashboardWidget[]
  /** When false, hidden from public share links. Default true. */
  public?: boolean
}

/** Viewer-supplied params for explorer widgets (ANDed with author base_query). */
export type ExplorerViewerParams = {
  filters?: Array<{ field: string; operator: string; value?: unknown }>
  sort?: Record<string, number> | Array<{ column: string; direction: 'asc' | 'desc' }>
  page?: number
  limit?: number
  columns?: string[]
  cursor?: string
}

export type DashboardActionTriggerBody = {
  form?: Record<string, unknown>
  row?: Record<string, unknown>
}

export type DashboardActionTriggerResponse = {
  action_run_id: number
  run_id?: number | null
  job_run_id?: number | null
}

export type DashboardActionStatusResponse = {
  status: string
  item_count?: number | null
  duration_ms?: number | null
  error_message?: string | null
}

export type DashboardStepItem = {
  label: string
  subtext?: string
  current?: boolean
  completed?: boolean
}

export type DashboardPersonItem = {
  label: string
  name: string
  email?: string
  phone?: string
  placeholder?: string
  icon?: string
  gravatar?: boolean
  gravatar_url?: string
}

/**
 * Connector-agnostic option source for input-widget pickers. `options_source` is
 * a readonly call_method config resolved server-side; the *_expr fields map each
 * raw row to `{ label, value, subText }`. When `search.on_frontend` is set the
 * backend returns one bounded page and the picker filters it client-side.
 */
export type OptionSource = {
  options_source: unknown
  label_expr?: string
  value_expr?: string
  subtext_expr?: string
  search?: {
    param: string
    on_frontend?: boolean
    placeholder?: string
    empty_text?: string
  }
  transform_expr?: string
  depends_on?: string[]
}

export type DashboardFormFieldType = 'text' | 'number' | 'boolean' | 'select' | 'multi_select'

export type DashboardFormField = {
  key: string
  label?: string
  type: DashboardFormFieldType
  required?: boolean
  default?: unknown
  format?: string
  options?: { label: string; value: unknown }[]
  options_source?: OptionSource
  depends_on?: string[]
  hide_if_expr?: string
}

export type DashboardWidget = {
  id: string
  type: DashboardWidgetType
  title?: string
  description?: string
  width?: number
  refresh_interval_seconds?: number
  markdown?: string
  /** Heading widget only: `section` (default) or `callout`. */
  variant?: 'section' | 'callout'
  unit?: string
  value_expr?: string
  columns?: DashboardTableColumn[]
  steps?: DashboardStepItem[] | string
  people?: DashboardPersonItem[] | string
  // Input widgets (field_mapping, form)
  config_key?: string
  source?: OptionSource
  target?: OptionSource
  allow_transform?: boolean
  validation_expr?: string
  fields?: DashboardFormField[]
  transform_expr?: string
  // field_mapping v2
  cardinality?: 'one_to_one' | 'many_to_one'
  extra_columns?: FieldMappingExtraColumn[]
  status_expr?: string
  row_action?: string
  // action
  label?: string
  form?: { fields: DashboardFormField[] }
  allow_public?: boolean
  cooldown_seconds?: number
  /** Friendly "what will happen" text for the pre-run confirmation dialog. */
  confirm_message?: string
  on_success?: DashboardActionOnSuccess
  // task_progress
  task?: string
  stat_expr?: string
  // explorer
  data?: {
    connector?: string
    resource?: string
    base_query?: Record<string, unknown>
    [key: string]: unknown
  }
  default_sort?: Record<string, number>
  page_size?: number
  features?: string[]
}

export type JobConfigEntry = DashboardWidget & {
  type: 'field_mapping' | 'form'
  config_key: string
}

export type DashboardNavbarCta = {
  label: string
  href: string
  variant: 'button' | 'link'
  icon?: string
  text?: string
}

export type DashboardDefinition = {
  title?: string
  description?: string
  navbar_ctas?: DashboardNavbarCta[]
  refresh_interval_seconds?: number
  default_scope?: string
  allow_public_input?: boolean
  /** Multi-page layout. Parser normalizes legacy widgets[] into one page. */
  pages?: DashboardPage[]
  /** Flattened widgets across all pages (always populated by the parser). */
  widgets: DashboardWidget[]
}

export type DashboardSummary = {
  id: number
  job_id: number
  dashboard_template_id: number | null
  name: string
  description: string | null
  status: string
  created_at: string
  updated_at: string | null
  has_share_link: boolean
}

export type DashboardDetail = DashboardSummary & {
  dashboard: DashboardDefinition | null
}

export type WidgetDataResponse = {
  widget_id: string
  data: unknown
}

export type WidgetBatchItem = {
  data?: unknown
  error?: string
}

export type WidgetBatchDataResponse = {
  widgets: Record<string, WidgetBatchItem>
}

export type WidgetOption = {
  label: string
  value: string
  subText?: string
}

export type WidgetOptionsResponse = {
  options: WidgetOption[]
  next_cursor: string | null
}

export type DashboardConfigResponse = {
  /** Current job_config values keyed by each input widget's `config_key`. */
  config: Record<string, unknown>
}

export type JobConfigResponse = {
  /** Schema-backed config entries from the job snapshot, keyed by config key. */
  schema: Record<string, JobConfigEntry>
  /** Current persisted job_config values keyed by config key. */
  config: Record<string, unknown>
}

export type WidgetOptionsParams = {
  /**
   * Which option source within the widget to resolve.
   * - `field_mapping`: 'source' | 'target'
   * - `form` select fields: the field's `key`
   */
  side?: string
  q?: string
  cursor?: string
  /** Draft value for the current input widget, used by dependent option sources before save. */
  value?: unknown
}

export type ShareDashboardResponse = {
  share_token: string
  public_url: string
}

/** Non-mutating paginated list fetch for dropdowns (does not touch Pinia store state). */
export async function searchListResource<T>(
  path: string,
  params: { q?: string; cursor?: string; limit?: number; [key: string]: unknown } = {},
): Promise<{ data: T[]; next_cursor: string | null }> {
  const query = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') query.set(k, String(v))
  }
  const qs = query.toString()
  const res = await api.get<ListResponse<T>>(`${path}${qs ? `?${qs}` : ''}`)
  return { data: res.result, next_cursor: res.nextCursor ?? null }
}

export function searchConnectors(params: { q?: string; cursor?: string; limit?: number } = {}) {
  return searchListResource<Connector>('/connectors', { sort: 'created_at', order: 'desc', limit: 50, ...params })
}

export function searchJobs(params: { q?: string; cursor?: string; limit?: number } = {}) {
  return searchListResource<Job>('/jobs', { sort: 'created_at', order: 'desc', limit: 50, ...params })
}

export function searchTasks(params: { q?: string; cursor?: string; limit?: number } = {}) {
  return searchListResource<Task>('/tasks', { sort: 'created_at', order: 'desc', limit: 50, ...params })
}

export async function searchVaultSecrets(params: { q?: string; limit?: number } = {}) {
  const query = new URLSearchParams()
  if (params.q) query.set('q', params.q)
  if (params.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  const res = await api.get<{ available: boolean; secrets: string[] }>(`/vault-secrets${qs ? `?${qs}` : ''}`)
  return { data: res.secrets, next_cursor: null as string | null }
}

export function fetchWidgetsData(
  dashboardId: number,
  widgetIds?: string[],
  options?: { explorer_params?: Record<string, ExplorerViewerParams> },
): Promise<WidgetBatchDataResponse> {
  return api.post<WidgetBatchDataResponse>(`/dashboards/${dashboardId}/widgets/data`, {
    widget_ids: widgetIds,
    ...(options?.explorer_params ? { explorer_params: options.explorer_params } : {}),
  })
}

export function publicFetchWidgetsData(
  token: string,
  widgetIds?: string[],
  options?: { explorer_params?: Record<string, ExplorerViewerParams> },
): Promise<WidgetBatchDataResponse> {
  return api.post<WidgetBatchDataResponse>(
    `/public/dashboards/${encodeURIComponent(token)}/widgets/data`,
    {
      widget_ids: widgetIds,
      ...(options?.explorer_params ? { explorer_params: options.explorer_params } : {}),
    },
  )
}

export function fetchWidgetData(
  dashboardId: number,
  widgetId: string,
  params?: ExplorerViewerParams,
): Promise<WidgetDataResponse> {
  return api.post<WidgetDataResponse>(
    `/dashboards/${dashboardId}/widgets/${encodeURIComponent(widgetId)}/data`,
    params && Object.keys(params).length > 0 ? { explorer_params: params } : undefined,
  )
}

export function publicFetchWidgetData(
  token: string,
  widgetId: string,
  params?: ExplorerViewerParams,
): Promise<WidgetDataResponse> {
  return api.post<WidgetDataResponse>(
    `/public/dashboards/${encodeURIComponent(token)}/widgets/${encodeURIComponent(widgetId)}/data`,
    params && Object.keys(params).length > 0 ? { explorer_params: params } : undefined,
  )
}

export function triggerDashboardAction(
  dashboardId: number,
  widgetId: string,
  body: DashboardActionTriggerBody = {},
): Promise<DashboardActionTriggerResponse> {
  return api.post<DashboardActionTriggerResponse>(
    `/dashboards/${dashboardId}/widgets/${encodeURIComponent(widgetId)}/action`,
    body,
  )
}

export function fetchDashboardActionStatus(
  dashboardId: number,
  actionRunId: number,
): Promise<DashboardActionStatusResponse> {
  return api.get<DashboardActionStatusResponse>(
    `/dashboards/${dashboardId}/action-runs/${actionRunId}/status`,
  )
}

export function publicTriggerDashboardAction(
  token: string,
  widgetId: string,
  body: DashboardActionTriggerBody = {},
): Promise<DashboardActionTriggerResponse> {
  return api.post<DashboardActionTriggerResponse>(
    `/public/dashboards/${encodeURIComponent(token)}/widgets/${encodeURIComponent(widgetId)}/action`,
    body,
  )
}

export function publicFetchDashboardActionStatus(
  token: string,
  actionRunId: number,
): Promise<DashboardActionStatusResponse> {
  return api.get<DashboardActionStatusResponse>(
    `/public/dashboards/${encodeURIComponent(token)}/action-runs/${actionRunId}/status`,
  )
}

// ---------------------------------------------------------------------------
// Dashboard input-widget option/config helpers.
//
// Non-mutating fetches return a single page (`{ options, next_cursor }`) and do
// NOT touch Pinia store state — picker results live in local component state per
// the pagination rule. Options is a POST (the search/cursor/side travel in the
// body), so we can't reuse `searchListResource` (GET) directly.
// ---------------------------------------------------------------------------

export function fetchWidgetOptions(
  dashboardId: number,
  widgetId: string,
  params: WidgetOptionsParams = {},
): Promise<WidgetOptionsResponse> {
  return api.post<WidgetOptionsResponse>(
    `/dashboards/${dashboardId}/widgets/${widgetId}/options`,
    params,
  )
}

export function fetchDashboardConfig(dashboardId: number): Promise<DashboardConfigResponse> {
  return api.get<DashboardConfigResponse>(`/dashboards/${dashboardId}/config`)
}

export function saveWidgetConfig(
  dashboardId: number,
  widgetId: string,
  value: unknown,
): Promise<{ ok: true }> {
  return api.put<{ ok: true }>(`/dashboards/${dashboardId}/widgets/${widgetId}/config`, { value })
}

export function fetchJobConfig(jobId: number): Promise<JobConfigResponse> {
  return api.get<JobConfigResponse>(`/jobs/${jobId}/config`)
}

export function saveJobConfig(
  jobId: number,
  key: string,
  value: unknown,
): Promise<{ ok: true; value: unknown }> {
  return api.put<{ ok: true; value: unknown }>(
    `/jobs/${jobId}/config/${encodeURIComponent(key)}`,
    { value },
  )
}

export function saveJobConfigObject(
  jobId: number,
  config: Record<string, unknown>,
): Promise<{ ok: true; config: Record<string, unknown> }> {
  return api.put<{ ok: true; config: Record<string, unknown> }>(
    `/jobs/${jobId}/config`,
    { config },
  )
}

export function fetchJobConfigOptions(
  jobId: number,
  key: string,
  params: WidgetOptionsParams = {},
): Promise<WidgetOptionsResponse> {
  return api.post<WidgetOptionsResponse>(
    `/jobs/${jobId}/config/${encodeURIComponent(key)}/options`,
    params,
  )
}

export function publicFetchWidgetOptions(
  token: string,
  widgetId: string,
  params: WidgetOptionsParams = {},
): Promise<WidgetOptionsResponse> {
  return api.post<WidgetOptionsResponse>(
    `/public/dashboards/${encodeURIComponent(token)}/widgets/${widgetId}/options`,
    params,
  )
}

export function publicFetchDashboardConfig(token: string): Promise<DashboardConfigResponse> {
  return api.get<DashboardConfigResponse>(
    `/public/dashboards/${encodeURIComponent(token)}/config`,
  )
}

export function publicSaveWidgetConfig(
  token: string,
  widgetId: string,
  value: unknown,
): Promise<{ ok: true }> {
  return api.put<{ ok: true }>(
    `/public/dashboards/${encodeURIComponent(token)}/widgets/${widgetId}/config`,
    { value },
  )
}
